import React, { useState, useEffect, useCallback } from 'react';
import {
  getBillingPeriods,
  createBillingPeriod,
  getBillingPeriod,
  updateBillingLine,
  advanceBillingStatus,
  deleteBillingPeriod,
  createRevisedBillingPeriod,
  refreshFromSchedule,
  releaseRetainage,
  unreleaseRetainage,
  getProgressAlerts,
} from '../api';
import ConfirmDialog from './ConfirmDialog';
import useConfirm from '../hooks/useConfirm';
import BillingPaySidebar from './billing/BillingPaySidebar';
import BillingG702 from './billing/BillingG702';
import BillingG703 from './billing/BillingG703';
import BillingFooterActions from './billing/BillingFooterActions';
import { CreatePayAppModal, CompareModal, ReleaseRetainageModal } from './billing/BillingDialogs';

/* ──────────────────────────────────────────────────────────────────
 * BillingTab — orchestrator
 *
 * Full structural redesign from the Claude Design billing handoff
 * (`~/Downloads/design_handoff_billing/`). Replaces the bespoke
 * "Review & Finalize Billing" modal entirely with the split-panel
 * layout described in the handoff:
 *
 *   ┌─ 320px Pay App list ─┬─ Pay App detail (scrollable) ────┐
 *   │  + New              │  Region 1 — G702 header card     │
 *   │  Period cards…      │  Region 2 — G703 line table      │
 *   │                     │  Region 3 — Footer actions       │
 *   └─────────────────────┴─────────────────────────────────────┘
 *
 *   Finalize is **inline-confirm** (2s morph in the footer), not a
 *   modal. Compare-to-prior, Create Pay App, and Release Retainage
 *   are small purpose-built modals.
 *
 * Behavior preserved from the prior 1704-line file:
 *   - getBillingPeriods / getBillingPeriod / createBillingPeriod /
 *     updateBillingLine / advanceBillingStatus (= Finalize) /
 *     retryBillingCESync / deleteBillingPeriod /
 *     createRevisedBillingPeriod / refreshFromSchedule /
 *     releaseRetainage / unreleaseRetainage
 *   - Auto-refresh from schedule on selecting a Draft period
 *   - Inline-edit autosave on % complete + stored materials with
 *     pending dot + red error dot + tooltip on failure
 *   - Retainage release with undo/redo stacks
 *   - 409 sync-order handling (theming the confirm dialog instead of
 *     raw browser alert)
 *   - Date validation (year range 1900-2100, end >= start)
 *   - Double-submit guards on every async handler
 *   - Compare-to-prior flow before commit on Draft → Finalized
 *     (handled by the inline-confirm pattern; opens Compare modal
 *     only when explicitly invoked, not on first Finalize click)
 *   - URL hash mirrors `selectedPayAppId` for shareable links
 * ────────────────────────────────────────────────────────────────── */

const validateBillingDates = (f) => {
  // [label, value, required]. Period start/end are required; a native
  // <input type="date"> CLEARS its value to "" when an impossible date is
  // entered (e.g. 04/31 -- April has 30 days), so an empty required date
  // here usually means the user typed a non-existent calendar date. Catch
  // it with a clear message instead of letting the empty value reach the
  // backend, which only emits a generic "Missing required fields" error.
  const fields = [
    ['Period Start', f.period_start_date, true],
    ['Period End', f.period_end_date, true],
    ['Application Date', f.invoice_date, false],
  ];
  for (const [label, val, required] of fields) {
    if (!val) {
      if (required) {
        return `${label} is required. (If you entered a date like 04/31, ` +
          `that day doesn't exist and the field was cleared -- pick a ` +
          `real calendar date.)`;
      }
      continue;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
    if (!m) return `${label} is not a valid date.`;
    const year = parseInt(m[1], 10);
    if (year < 1900 || year > 2100) {
      return `${label} year (${year}) is outside the allowed range (1900-2100).`;
    }
    const d = new Date(val + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return `${label} is not a valid date.`;
  }
  if (f.period_start_date && f.period_end_date && f.period_end_date < f.period_start_date) {
    return 'Period End must be on or after Period Start.';
  }
  return '';
};

const DocIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

const DollarBigIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const BillingTab = ({ projectId, refreshSummary }) => {
  const { confirm, confirmDialogProps } = useConfirm();

  /* ───── State ───── */
  const [periods, setPeriods] = useState([]);
  const [selPeriod, setSelPeriod] = useState(null);
  const [details, setDetails] = useState(null);
  const [progressAlerts, setProgressAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [creatingPeriod, setCreatingPeriod] = useState(false);
  const [createFormError, setCreateFormError] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [prevDetails, setPrevDetails] = useState(null);
  const [releaseModal, setReleaseModal] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);

  // Async-action guards
  const [advancing, setAdvancing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [revising, setRevising] = useState(false);
  // CE sync retry state lived here previously (retryingPeriodId,
  // handleRetryCESync). Removed alongside the visible retry button +
  // chip. Will be reintroduced in a dedicated CE sync admin tab —
  // retryBillingCESync is still exported from api/index.js.

  // Inline-edit feedback
  const [pendingSaves, setPendingSaves] = useState(new Set());
  const [saveErrors, setSaveErrors] = useState(new Map());

  // Undo/redo for retainage release + line edits
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  /* ───── Fetchers ───── */
  const fetchPeriods = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBillingPeriods(projectId);
      setPeriods(data);
      return data;
    } catch (e) { console.error(e); return []; }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchPeriods(); }, [fetchPeriods]);

  const fetchDetails = async (periodId) => {
    try {
      const data = await getBillingPeriod(periodId);
      setDetails(data);
    } catch (e) { console.error(e); }
    // Schedule-progress alerts — flags SOV lines whose linked tasks are
    // behind their expected (date-prorated) progress. Drives the rose
    // (critical, 20%+ behind) / yellow (warning, 10%+ behind) row
    // highlighting + banner in the G703. Fetched alongside details so the
    // flags stay fresh after each inline % edit. Failure is non-fatal:
    // clears alerts and the table still renders normally.
    try {
      const al = await getProgressAlerts(periodId);
      setProgressAlerts(al);
    } catch (e) {
      console.error('Failed to fetch progress alerts:', e);
      setProgressAlerts(null);
    }
  };

  /* ───── URL hash sync ───── */
  useEffect(() => {
    // Read initial selection from hash on first load.
    if (!periods?.length) return;
    if (selPeriod) {
      // Mirror selection back into hash.
      const h = `#afp-${selPeriod.id}`;
      if (window.location.hash !== h) {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${h}`);
      }
      return;
    }
    const m = /^#afp-(\d+)$/.exec(window.location.hash);
    if (m) {
      const id = parseInt(m[1], 10);
      const p = periods.find(x => x.id === id);
      if (p) selectPeriod(p);
      return;
    }
    // Default: select the most-recent period
    const sorted = [...periods].sort((a, b) =>
      (b.period_end_date || '').localeCompare(a.period_end_date || ''),
    );
    if (sorted[0]) selectPeriod(sorted[0]);
    // eslint-disable-next-line
  }, [periods]);

  const selectPeriod = async (p) => {
    setSelPeriod(p);
    setDetails(null);
    setProgressAlerts(null);
    setPendingSaves(new Set());
    setSaveErrors(new Map());
    if (p?.status === 'Draft') {
      try {
        setRefreshing(true);
        await refreshFromSchedule(p.id);
      } catch (e) { console.error('Auto-refresh from schedule failed:', e); }
      finally { setRefreshing(false); }
    }
    fetchDetails(p.id);
  };

  /* ───── Pay app CRUD ───── */
  const handleCreate = async (form) => {
    if (creatingPeriod) return;
    const err = validateBillingDates(form);
    if (err) { setCreateFormError(err); return; }
    setCreateFormError('');
    try {
      setCreatingPeriod(true);
      const created = await createBillingPeriod({ ...form, project_id: projectId });
      setShowCreate(false);
      const ps = await fetchPeriods();
      refreshSummary && refreshSummary();
      const newPeriod = (created && created.id)
        ? (ps.find(p => p.id === created.id) || created)
        : ps[0];
      if (newPeriod) selectPeriod(newPeriod);
    } catch (err) {
      console.error(err);
      setCreateFormError(err?.message || 'Failed to create pay application.');
    } finally {
      setCreatingPeriod(false);
    }
  };

  const handleDelete = async () => {
    if (!selPeriod || deleting) return;
    const ok = await confirm({
      title: 'Delete Pay Application',
      message: `Delete "${selPeriod.period_name || `AFP #${selPeriod.id}`}"? This cannot be undone.`,
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    try {
      setDeleting(true);
      await deleteBillingPeriod(selPeriod.id);
      setSelPeriod(null); setDetails(null);
      await fetchPeriods();
      refreshSummary && refreshSummary();
    } catch (e) {
      console.error(e);
      alert('Failed to delete pay application: ' + (e?.message || 'Unknown error'));
    } finally { setDeleting(false); }
  };

  const handleRevise = async () => {
    if (!selPeriod || revising) return;
    try {
      setRevising(true);
      const revised = await createRevisedBillingPeriod(selPeriod.id);
      await fetchPeriods();
      setSelPeriod(revised);
      await fetchDetails(revised.id);
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to create revised pay application.');
    } finally { setRevising(false); }
  };

  /* ───── Inline edit + autosave ───── */
  const handleUpdateLine = async (lineId, field, value) => {
    const key = `${lineId}:${field}`;
    setPendingSaves(prev => { const n = new Set(prev); n.add(key); return n; });
    setSaveErrors(prev => { const n = new Map(prev); n.delete(key); return n; });
    try {
      const prevLine = details?.lines?.find(l => l.id === lineId);
      const prevValue = prevLine ? prevLine[field] : null;
      setUndoStack(prev => [...prev, { lineId, field, value: prevValue }]);
      setRedoStack([]);
      await updateBillingLine(lineId, { [field]: value });
      await fetchDetails(selPeriod.id);
      refreshSummary && refreshSummary();
    } catch (e) {
      console.error(e);
      setSaveErrors(prev => {
        const n = new Map(prev);
        n.set(key, e?.message || 'Save failed');
        return n;
      });
    } finally {
      setPendingSaves(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  /* ───── Finalize flow (inline-confirm in the footer triggers this) ───── */
  const doAdvance = async () => {
    if (advancing) return;
    try {
      setAdvancing(true);
      const periodId = selPeriod.id;
      await advanceBillingStatus(periodId);
      const ps = await fetchPeriods();
      const u = ps?.find(p => p.id === periodId);
      if (u) { setSelPeriod(u); await fetchDetails(u.id); }
      refreshSummary && refreshSummary();
    } catch (e) {
      console.error(e);
      // 409 = ordering block: earlier period needs CE sync first
      if (e?.status === 409 && e?.body?.blocking_period_name) {
        await confirm({
          title: 'Sync Order Required',
          message: e.body.error || e.message,
          confirmLabel: 'OK', danger: false,
        });
      } else {
        alert(e.message || 'Failed to finalize.');
      }
    } finally { setAdvancing(false); }
  };

  /* ───── Retainage release ───── */
  const openReleaseModal = (line) => {
    // Use the SERVER-computed retainage figures (retainage_held /
    // retainage_available), which are derived with the exact formula the
    // release endpoint enforces. The old code used the stored
    // bpl.retainage_amount (this-period only, can go stale), so "Release
    // all" could offer more than the backend would accept -> "Only $X
    // available" rejection.
    const totalHeld = parseFloat(line.retainage_held) || 0;
    const alreadyReleased = parseFloat(line.retainage_released_cumulative) || 0;
    const available = Math.max(0, parseFloat(line.retainage_available) || 0);
    const base = parseFloat(line.retainage_base) || 0;
    // Held as a % of the work it's held against (≈ the retainage rate) —
    // sensible now that held + base come from the same earned figure.
    const pctOfCompleted = base > 0
      ? ((totalHeld / base) * 100).toFixed(1)
      : '0.0';
    setReleaseModal({
      sovLineId: line.sov_line_id,
      lineDesc: line.sov_description,
      sovCode: line.sov_code,
      totalHeld,
      alreadyReleased,
      available,
      pctOfCompleted,
    });
  };

  const handleReleaseRetainage = async ({ amount: targetAmt, notes }) => {
    if (!releaseModal) return;
    if (isNaN(targetAmt) || targetAmt < 0) return;
    if (targetAmt > releaseModal.totalHeld + 0.01) return;
    const currentReleased = releaseModal.alreadyReleased;
    const delta = targetAmt - currentReleased;
    if (Math.abs(delta) < 0.01) { setReleaseModal(null); return; }
    try {
      setReleaseLoading(true);
      const prevReleased = currentReleased;
      if (delta > 0) {
        await releaseRetainage(releaseModal.sovLineId, { amount: delta, notes });
      } else {
        await unreleaseRetainage(releaseModal.sovLineId, { amount: Math.abs(delta) });
      }
      setUndoStack(prev => [...prev, {
        type: 'retainage_set',
        sovLineId: releaseModal.sovLineId,
        previousReleased: prevReleased,
        newReleased: targetAmt,
      }]);
      setRedoStack([]);
      setReleaseModal(null);
      if (selPeriod) {
        if (selPeriod.status === 'Draft') await refreshFromSchedule(selPeriod.id);
        await fetchDetails(selPeriod.id);
      }
      refreshSummary && refreshSummary();
    } catch (err) {
      console.error('Failed to set retainage release:', err);
      alert('Failed to release retainage: ' + (err?.message || 'Unknown error'));
    } finally { setReleaseLoading(false); }
  };

  /* ───── Compare modal (prior period diff) ───── */
  const openCompare = async () => {
    if (!selPeriod) return;
    const prior = periods
      .filter(p => p.id !== selPeriod.id && p.status === 'Finalized')
      .sort((a, b) => (b.period_end_date || '').localeCompare(a.period_end_date || ''))[0];
    if (prior) {
      try { setPrevDetails(await getBillingPeriod(prior.id)); }
      catch { setPrevDetails(null); }
    } else {
      setPrevDetails(null);
    }
    setShowCompare(true);
  };

  const handleExportCSV = () => {
    if (!selPeriod) return;
    const url = `/api/billing/${selPeriod.id}/ce-export`;
    window.open(url, '_blank');
  };

  /* ───── Derived ───── */
  const isDraft = selPeriod?.status === 'Draft';
  const nextPayAppNum = (periods?.length || 0) + 1;

  /* ───── Render ───── */
  return (
    <div style={{
      // Negative horizontal margin escapes .app-main's 24px padding
      // so the billing split-panel uses the full chrome width (sidebar
      // + detail surface). Without this the G703 12-column grid would
      // clip on standard laptop widths because .app-main has
      // overflow-x: hidden. 24px side padding lives ON this element so
      // there's still breathing room from the viewport edge.
      // Tab body sits inside .app-main's 24px gutter so the sidebar +
      // detail align with the chrome's outer edges above (also at
      // 24px from viewport). No internal horizontal padding — the
      // gutter provides external breathing room and inner cards
      // (G702 / G703 / sidebar / footer) align with the chrome's
      // left edge for a clean column.
      padding: '20px 0 32px',
      background: 'var(--bg-page)',
      minHeight: '100%',
      display: 'flex', gap: 16,
      alignItems: 'flex-start',
    }}>
      {/* LEFT — Pay app sidebar */}
      <BillingPaySidebar
        periods={periods}
        selectedId={selPeriod?.id}
        onSelect={selectPeriod}
        onCreate={() => { setCreateFormError(''); setShowCreate(true); }}
        loading={loading}
      />

      {/* RIGHT — Pay app detail */}
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        {!selPeriod ? (
          /* No selection — quiet center-stage prompt */
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-sm)',
            padding: '60px 24px',
            textAlign: 'center',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
            color: 'var(--text-3)',
            minHeight: 360,
            justifyContent: 'center',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 14,
              background: 'var(--accent-soft)', color: 'var(--accent-text)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {periods.length === 0 ? <DollarBigIcon /> : <DocIcon />}
            </div>
            <div style={{ fontSize: 16.5, fontWeight: 500, color: 'var(--text-1)' }}>
              {periods.length === 0 ? 'No pay applications yet' : 'Select a pay application'}
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 380, lineHeight: 1.45 }}>
              {periods.length === 0
                ? 'Create one to start billing this project. AFP #1 will baseline the SOV in ComputerEase on finalize.'
                : 'Pick a period from the list on the left to review the G702/G703 and finalize when ready.'}
            </div>
            {periods.length === 0 && (
              <button
                type="button"
                onClick={() => { setCreateFormError(''); setShowCreate(true); }}
                style={{
                  marginTop: 4,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 34, padding: '0 14px', borderRadius: 8,
                  fontSize: 14.5, fontWeight: 500, color: '#fff',
                  background: 'var(--accent)',
                  border: '1px solid color-mix(in oklch, var(--accent) 50%, black)',
                  boxShadow: '0 1px 0 color-mix(in oklch, var(--accent) 35%, black), 0 1px 3px oklch(0.3 0.05 135 / 0.18)',
                  cursor: 'pointer',
                }}
              >
                Create first pay application
              </button>
            )}
          </div>
        ) : !details ? (
          /* Loading the selected period */
          <div style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-sm)',
            padding: '60px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            color: 'var(--text-3)', fontSize: 14.5,
            minHeight: 360,
          }}>
            <span style={{
              display: 'inline-block', width: 20, height: 20, borderRadius: 99,
              border: '2px solid color-mix(in oklch, var(--accent) 30%, transparent)',
              borderTopColor: 'var(--accent)',
              animation: 'spin .8s linear infinite',
            }} />
            {refreshing ? 'Refreshing from schedule…' : 'Loading pay application…'}
          </div>
        ) : (
          <>
            <BillingG702 period={selPeriod} details={details} />

            {/* Action bar (Delete · Finalize on draft / Export CSV ·
                Revise on finalized) sits ABOVE the G703 continuation
                sheet so it's reachable without scrolling past the
                line table. Click Finalize → opens the Review &
                Finalize modal (4 summary tiles + line table + prior-
                period diff if available); the actual commit happens
                from that modal's "Finalize & Send to Accounting"
                button which calls doAdvance. */}
            <BillingFooterActions
              period={selPeriod}
              onDelete={handleDelete}
              onFinalize={openCompare}
              onExportCSV={handleExportCSV}
              onRevise={handleRevise}
              finalizing={advancing}
              deleting={deleting}
              revising={revising}
            />

            <BillingG703
              details={details}
              isDraft={isDraft}
              onUpdateLine={handleUpdateLine}
              onReleaseRetainage={openReleaseModal}
              pendingSaves={pendingSaves}
              saveErrors={saveErrors}
              progressAlerts={progressAlerts}
            />
          </>
        )}
      </div>

      {/* ─────────── Dialogs ─────────── */}
      <CreatePayAppModal
        open={showCreate}
        onClose={() => !creatingPeriod && setShowCreate(false)}
        onSubmit={handleCreate}
        defaultPayAppNum={nextPayAppNum}
        submitting={creatingPeriod}
        errorMsg={createFormError}
      />

      <CompareModal
        open={showCompare}
        onClose={() => setShowCompare(false)}
        current={details}
        prior={prevDetails}
        onFinalize={isDraft ? () => { setShowCompare(false); doAdvance(); } : null}
        finalizing={advancing}
      />

      <ReleaseRetainageModal
        open={!!releaseModal}
        payload={releaseModal}
        onClose={() => !releaseLoading && setReleaseModal(null)}
        onSubmit={handleReleaseRetainage}
        submitting={releaseLoading}
      />

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default BillingTab;
